import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import api, { formatApiErrorDetail } from "../../lib/api";
import AdminPageHeader from "./AdminPageHeader";

const CADENCE_FIELDS = [
  { key: "sla_stale_days", label: "SLA stale days", min: 1, max: 180, hint: "Cases with no activity for > N days are flagged stale." },
  { key: "sla_escalate_days", label: "SLA escalate days", min: 1, max: 365, hint: "Cases not closed within N days are auto-escalated." },
  { key: "cadence_high_risk_days", label: "High-risk cadence (days)", min: 1, max: 90, hint: "Reminder cadence for high-risk open cases." },
  { key: "cadence_escalated_days", label: "Escalated cadence (days)", min: 1, max: 90, hint: "Reminder cadence for escalated open cases." },
  { key: "cadence_standard_days", label: "Standard cadence (days)", min: 1, max: 180, hint: "Reminder cadence for ordinary open cases." },
];

const CHANNELS = ["inapp", "email", "teams", "slack"];

export default function NotificationsAdmin() {
  const { readOnly } = useOutletContext();
  const [cadence, setCadence] = useState({});
  const [routing, setRouting] = useState([]);
  const [draft, setDraft] = useState({});

  const load = async () => {
    try {
      const [c, r] = await Promise.all([
        api.get("/admin/notifications/cadence"),
        api.get("/admin/notifications/routing"),
      ]);
      setCadence(c.data);
      setDraft(c.data);
      setRouting(r.data.routing);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const saveCadence = async () => {
    if (readOnly) return;
    const changed = Object.fromEntries(
      Object.entries(draft).filter(([k, v]) => v !== cadence[k])
    );
    if (Object.keys(changed).length === 0) return;
    try {
      const { data } = await api.patch("/admin/notifications/cadence", changed);
      setCadence(data);
      setDraft(data);
      toast.success("Cadence updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const toggleRoute = async (type, channel, enabled) => {
    if (readOnly) return;
    try {
      await api.patch("/admin/notifications/routing", { type, channel, enabled });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const types = Array.from(new Set(routing.map((r) => r.type)));

  return (
    <div data-testid="admin-notifications-page" className="space-y-5">
      <AdminPageHeader
        eyebrow="Admin · Notifications"
        title="Notifications"
        description="Cadence thresholds and channel routing for proactive case reminders."
      />

      <div className="brand-card p-4">
        <h2 className="text-base font-semibold text-[color:var(--page-fg)]">Cadence</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {CADENCE_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="text-xs font-medium text-[color:var(--page-fg-muted)]">{f.label}</label>
              <Input
                type="number"
                min={f.min}
                max={f.max}
                value={draft[f.key] ?? ""}
                onChange={(e) => setDraft((d) => ({ ...d, [f.key]: Number(e.target.value) }))}
                disabled={readOnly}
                className="mt-1 h-9 w-32"
                aria-label={f.label}
                data-testid={`cadence-${f.key}`}
              />
              <p className="mt-1 text-[11px] text-[color:var(--page-fg-muted)]">{f.hint}</p>
            </div>
          ))}
        </div>
        {!readOnly && (
          <Button
            onClick={saveCadence}
            data-testid="cadence-save"
            className="mt-4 goodwin-primary-btn text-white"
          >
            Save cadence
          </Button>
        )}
      </div>

      <div className="brand-card overflow-x-auto p-4">
        <h2 className="text-base font-semibold text-[color:var(--page-fg)]">Routing matrix</h2>
        <p className="mt-1 text-xs text-[color:var(--page-fg-muted)]">Which channels deliver each notification type.</p>
        <table className="mt-3 min-w-full border-collapse text-sm">
          <caption className="sr-only">Notification routing matrix</caption>
          <thead>
            <tr>
              <th className="px-3 py-2 text-left text-xs uppercase text-[color:var(--page-fg-muted)]" scope="col">Type</th>
              {CHANNELS.map((ch) => (
                <th key={ch} className="px-3 py-2 text-xs uppercase text-[color:var(--page-fg-muted)]" scope="col">{ch}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[color:var(--border-subtle)]">
            {types.map((t) => (
              <tr key={t}>
                <th className="px-3 py-2 text-left text-sm font-semibold text-[color:var(--page-fg)]" scope="row">{t}</th>
                {CHANNELS.map((ch) => {
                  const row = routing.find((r) => r.type === t && r.channel === ch);
                  return (
                    <td key={ch} className="px-3 py-2 text-center" data-testid={`routing-cell-${t}-${ch}`}>
                      <Switch
                        checked={!!row?.enabled}
                        onCheckedChange={(v) => toggleRoute(t, ch, v)}
                        disabled={readOnly}
                        aria-label={`${t} via ${ch}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
